import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading, StatCard } from "../../components/VB";
import RupiahInput from "../../components/RupiahInput";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Users, Store, Receipt, Wallet, CreditCard, User, FileSpreadsheet, Package } from "lucide-react";

const fmtIDR = (n) => "Rp " + (parseFloat(n || 0)).toLocaleString("id-ID", { maximumFractionDigits: 0 });
const fmtMonthID = (value) => {
  if (!value) return "Semua Periode";
  const d = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
};
const fmtDateTimeID = (value) => {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return value || "";
  return d.toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" });
};
const escapeHtml = (value) => String(value ?? "").replace(/[&<>'"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" }[c]));
const fmtUnit = (p) => {
  const ukuran = String(p?.ukuran_satuan || "").trim();
  const satuan = String(p?.satuan || "").trim();
  if (ukuran && satuan) return `${ukuran} ${satuan}`;
  return ukuran || satuan || "-";
};
const formatTrxItems = (items) => (Array.isArray(items) ? items : []).map((i) => {
  const unit = fmtUnit(i);
  return `${i.nama || "Produk"}${unit !== "-" ? ` (${unit})` : ""} ×${i.qty || 0}`;
}).join(", ");
const printAdminKoperasiReport = (items, filters = {}) => {
  const rowsData = Array.isArray(items) ? items : [];
  if (rowsData.length === 0) return toast.error("Tidak ada data transaksi yang tampil untuk dicetak");
  const printWindow = window.open("", "_blank", "width=1024,height=768");
  if (!printWindow) return toast.error("Pop-up print diblokir browser. Izinkan pop-up lalu coba lagi.");

  const total = rowsData.reduce((a, t) => a + Number(t.total || 0), 0);
  const totalTerhutang = rowsData.filter((t) => t.status === "Terhutang").reduce((a, t) => a + Number(t.total || 0), 0);
  const totalPayroll = rowsData.filter((t) => t.status !== "Terhutang").reduce((a, t) => a + Number(t.total || 0), 0);
  const totalVendorPaid = rowsData.filter((t) => t.vendor_payment_status === "Lunas").reduce((a, t) => a + Number(t.total || 0), 0);
  const totalVendorPending = rowsData.filter((t) => t.vendor_payment_status !== "Lunas").reduce((a, t) => a + Number(t.total || 0), 0);
  const vendorLabel = filters.vendorLabel || "Semua Vendor";
  const rows = rowsData.map((t, idx) => `
    <tr>
      <td class="center">${idx + 1}</td>
      <td>${escapeHtml(t.no_transaksi || "-")}</td>
      <td>${escapeHtml(t.tanggal || "-")}</td>
      <td>${escapeHtml(t.employee_nama || "-")}<br><span class="muted">${escapeHtml(t.no_karyawan || t.no_rekening_koperasi || "")}</span></td>
      <td>${escapeHtml(t.vendor_nama || "-")}</td>
      <td>${escapeHtml(formatTrxItems(t.items))}</td>
      <td class="right">${escapeHtml(fmtIDR(t.total || 0))}</td>
      <td class="center"><span class="badge ${t.status === "Terhutang" ? "pending" : "paid"}">${escapeHtml(t.status || "-")}</span></td>
      <td class="center"><span class="badge ${t.vendor_payment_status === "Lunas" ? "paid" : "pending"}">${escapeHtml(t.vendor_payment_status === "Lunas" ? "Lunas ke Vendor" : "Pending ke Vendor")}</span></td>
    </tr>`).join("");

  printWindow.document.write(`<!doctype html>
<html>
<head>
  <title>Laporan Transaksi Pembelian Koperasi - ${escapeHtml(fmtMonthID(filters.bulan))}</title>
  <style>
    @page { size: A4 landscape; margin: 10mm; }
    * { box-sizing: border-box; }
    body { margin: 0; color: #000; background: #fff; font-family: Arial, Helvetica, sans-serif; font-size: 10.5px; line-height: 1.35; }
    .kop-yellow { background: #F2C744; height: 18px; margin-bottom: 8px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .kop { display: flex; align-items: center; gap: 14px; }
    .kop img { width: 70px; height: 70px; object-fit: contain; }
    .kop-text { flex: 1; font-family: "Times New Roman", Times, serif; }
    .company { font-size: 19px; font-weight: 700; letter-spacing: .3px; }
    .tagline { font-size: 12px; font-style: italic; margin-top: 2px; }
    .addr { font-size: 11px; margin-top: 3px; }
    .kop-line { border-bottom: 2.5px solid #000; margin-top: 8px; }
    h1 { text-align: center; font-size: 15px; margin: 12px 0 2px; text-transform: uppercase; }
    .subtitle { text-align: center; font-size: 12px; margin-bottom: 10px; }
    .meta { display: grid; grid-template-columns: 112px 10px 1fr 120px 10px 1fr; gap: 3px 4px; margin: 8px 0 10px; }
    .summary { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; margin: 8px 0 10px; }
    .card { border: 1px solid #777; padding: 6px; min-height: 45px; }
    .card-label { font-size: 9.5px; color: #333; }
    .card-value { font-size: 13px; font-weight: 700; margin-top: 3px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #555; padding: 4px; vertical-align: top; }
    th { background: #e9eef7; text-align: center; font-weight: 700; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .center { text-align: center; }
    .right { text-align: right; }
    .muted { color: #555; font-size: 9.5px; }
    .badge { display: inline-block; padding: 2px 5px; border: 1px solid; font-weight: 700; font-size: 9.5px; white-space: nowrap; }
    .paid { color: #1f7a1f; background: #e5f7e5; border-color: #1f7a1f; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .pending { color: #9f6000; background: #fff3cd; border-color: #9f6000; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .footer-note { margin-top: 10px; font-size: 10px; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
  </style>
</head>
<body>
  <div class="kop-yellow"></div>
  <div class="kop">
    <img src="/logo-fivehoster.png" alt="PT. Fivehoster">
    <div class="kop-text">
      <div class="company">PT. FIVEHOSTER KARYA BANGSA</div>
      <div class="tagline">Jasa Penyedia Server, Pembuatan Software dan Website</div>
      <div class="addr">Head Office : Jl. Sawah Lunto RT.27 RW.07 Jeru Kec.Turen Kab.Malang</div>
      <div class="addr">Telp/Email : (031) 30004702 – support@fivehoster.co.id</div>
    </div>
  </div>
  <div class="kop-line"></div>

  <h1>Laporan Transaksi Pembelian Koperasi</h1>
  <div class="subtitle">Rekap pembelian karyawan, status potong gaji, dan status payment perusahaan ke vendor</div>

  <div class="meta">
    <div>Periode</div><div>:</div><strong>${escapeHtml(fmtMonthID(filters.bulan))}</strong>
    <div>Vendor</div><div>:</div><strong>${escapeHtml(vendorLabel)}</strong>
    <div>Filter Nama</div><div>:</div><strong>${escapeHtml(filters.nama || "Semua Nama")}</strong>
    <div>Status Transaksi</div><div>:</div><strong>${escapeHtml(filters.status || "Semua Status")}</strong>
    <div>Jumlah Data</div><div>:</div><strong>${rowsData.length} transaksi</strong>
    <div>Tanggal Cetak</div><div>:</div><strong>${escapeHtml(fmtDateTimeID(new Date().toISOString()))}</strong>
  </div>

  <div class="summary">
    <div class="card"><div class="card-label">Total Pembelian</div><div class="card-value">${escapeHtml(fmtIDR(total))}</div></div>
    <div class="card"><div class="card-label">Status Terhutang</div><div class="card-value">${escapeHtml(fmtIDR(totalTerhutang))}</div></div>
    <div class="card"><div class="card-label">Lunas Payroll</div><div class="card-value">${escapeHtml(fmtIDR(totalPayroll))}</div></div>
    <div class="card"><div class="card-label">Dibayar ke Vendor</div><div class="card-value">${escapeHtml(fmtIDR(totalVendorPaid))}</div></div>
    <div class="card"><div class="card-label">Pending ke Vendor</div><div class="card-value">${escapeHtml(fmtIDR(totalVendorPending))}</div></div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width: 28px;">No</th>
        <th style="width: 90px;">No. Trx</th>
        <th style="width: 64px;">Tanggal</th>
        <th style="width: 110px;">Karyawan</th>
        <th style="width: 95px;">Vendor</th>
        <th>Items</th>
        <th style="width: 82px;">Total</th>
        <th style="width: 90px;">Status</th>
        <th style="width: 105px;">Payment Vendor</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="footer-note">Laporan ini dicetak berdasarkan data yang tampil pada tabel Transaksi Admin setelah filter bulan, vendor, nama, dan status diterapkan.</div>
  <script>window.onload = function(){ window.focus(); window.print(); };</script>
</body>
</html>`);
  printWindow.document.close();
};

export default function Koperasi() {
  const [tab, setTab] = useState("anggota");
  return (
    <Window
      title="Koperasi Karyawan FIVEHOSTER"
      actions={
        <>
          <button className={`vb-btn ${tab === "anggota" ? "vb-btn-primary" : ""}`} onClick={() => setTab("anggota")} data-testid="kop-tab-anggota"><Users className="w-3 h-3 inline mr-1" />Anggota</button>
          <button className={`vb-btn ${tab === "vendor" ? "vb-btn-primary" : ""}`} onClick={() => setTab("vendor")} data-testid="kop-tab-vendor"><Store className="w-3 h-3 inline mr-1" />Vendor</button>
          <button className={`vb-btn ${tab === "transaksi" ? "vb-btn-primary" : ""}`} onClick={() => setTab("transaksi")} data-testid="kop-tab-trx"><Receipt className="w-3 h-3 inline mr-1" />Transaksi</button>
          <button className={`vb-btn ${tab === "payment" ? "vb-btn-primary" : ""}`} onClick={() => setTab("payment")} data-testid="kop-tab-pay"><Wallet className="w-3 h-3 inline mr-1" />Payment Vendor</button>
          <button className={`vb-btn ${tab === "payroll" ? "vb-btn-primary" : ""}`} onClick={() => setTab("payroll")} data-testid="kop-tab-payroll"><CreditCard className="w-3 h-3 inline mr-1" />Apply Payroll</button>
        </>
      }
    >
      {tab === "anggota" && <AnggotaTab />}
      {tab === "vendor" && <VendorTab />}
      {tab === "transaksi" && <TransaksiTab />}
      {tab === "payment" && <PaymentVendorTab />}
      {tab === "payroll" && <PayrollPotonganTab />}
    </Window>
  );
}

// ============ ANGGOTA TAB ============
function AnggotaTab() {
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { employee_id: "", no_rekening_koperasi: "", rfid_card: "", plafon: 0, status_aktif: true, catatan: "" };
  const [form, setForm] = useState(empty);
  const [del, setDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([api.get("/koperasi/anggota"), api.get("/employees")]);
      setItems(a.data); setEmps(e.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, plafon: parseFloat(form.plafon || 0) };
      if (editId) await api.put(`/koperasi/anggota/${editId}`, payload);
      else await api.post("/koperasi/anggota", payload);
      toast.success("Tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/koperasi/anggota/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <Loading />;
  return (
    <>
      <div style={{ marginBottom: 10 }}>
        <button className="vb-btn-primary" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-anggota">
          <Plus className="w-3 h-3 inline mr-1" />Daftarkan Anggota
        </button>
        <span style={{ fontSize: 11, color: "#666", marginLeft: 10 }}>Plafon = limit pembelian per anggota. Akan dipotong dari gaji tgl 17 bulan berikutnya.</span>
      </div>
      {items.length === 0 ? <EmptyState message="Belum ada anggota koperasi" /> : (
        <table className="vb-table">
          <thead><tr><th>No. Rek Kop</th><th>RFID</th><th>Karyawan</th><th style={{ textAlign: "right" }}>Plafon</th><th style={{ textAlign: "right" }}>Terhutang</th><th style={{ textAlign: "right" }}>Sisa Plafon</th><th>Status</th><th>Aksi</th></tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id}>
                <td style={{ fontFamily: "Consolas, monospace", fontWeight: 600 }}>{a.no_rekening_koperasi}</td>
                <td style={{ fontFamily: "Consolas, monospace" }}>{a.rfid_card}</td>
                <td>{a.employee_nama} <span style={{ fontSize: 10, color: "#666" }}>({a.no_karyawan})</span></td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtIDR(a.plafon)}</td>
                <td style={{ textAlign: "right", color: a.terhutang > 0 ? "#d8000c" : "#666" }}>{fmtIDR(a.terhutang)}</td>
                <td style={{ textAlign: "right", color: a.sisa_plafon > 0 ? "#4f8a10" : "#d8000c", fontWeight: 600 }}>{fmtIDR(a.sisa_plafon)}</td>
                <td>{a.status_aktif ? <span className="vb-badge vb-badge-hadir">Aktif</span> : <span className="vb-badge vb-badge-tidakhadir">Nonaktif</span>}</td>
                <td>
                  <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => { setForm(a); setEditId(a.id); setOpen(true); }}><Pencil className="w-3 h-3" /></button>
                  <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(a)}><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit Anggota Koperasi" : "Daftarkan Anggota Koperasi"} width={500}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={save} data-testid="anggota-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Karyawan*</label>
          <select className="vb-select" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} disabled={!!editId} data-testid="anggota-emp">
            <option value="">- pilih karyawan -</option>
            {emps.map((e) => <option key={e.id} value={e.id}>{e.no_karyawan} - {e.nama_lengkap}</option>)}
          </select></div>
        <div className="vb-form-row"><label className="vb-label">No. Rekening Koperasi*</label>
          <input className="vb-input" value={form.no_rekening_koperasi} onChange={(e) => setForm({ ...form, no_rekening_koperasi: e.target.value })} placeholder="Mis: KOP-001" data-testid="anggota-norek" /></div>
        <div className="vb-form-row"><label className="vb-label">Kartu RFID*</label>
          <input className="vb-input" value={form.rfid_card} onChange={(e) => setForm({ ...form, rfid_card: e.target.value })} placeholder="Mis: RFID-A1B2C3" data-testid="anggota-rfid" /></div>
        <div className="vb-form-row"><label className="vb-label">Plafon (IDR)*</label>
          <RupiahInput value={form.plafon} onChange={(v) => setForm({ ...form, plafon: v })} testId="anggota-plafon" /></div>
        <div className="vb-form-row">
          <label style={{ fontSize: 12 }}><input type="checkbox" checked={!!form.status_aktif} onChange={(e) => setForm({ ...form, status_aktif: e.target.checked })} /> Anggota aktif</label>
        </div>
        <div className="vb-form-row"><label className="vb-label">Catatan</label>
          <textarea rows={2} className="vb-textarea" value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} /></div>
      </Dialog>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus anggota "${del?.employee_nama}"?`} />
    </>
  );
}

// ============ VENDOR TAB ============
function VendorTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { nama: "", pemilik: "", kontak: "", email: "", alamat: "", bank_nama: "", bank_rekening: "", bank_atas_nama: "", status_aktif: true, catatan: "" };
  const [form, setForm] = useState(empty);
  const [del, setDel] = useState(null);
  const [acctTarget, setAcctTarget] = useState(null);
  const [acctForm, setAcctForm] = useState({ email: "", password: "password123" });
  const [produkTarget, setProdukTarget] = useState(null); // vendor yang lagi dilihat daftar produknya
  const [produkList, setProdukList] = useState([]);
  const [produkLoading, setProdukLoading] = useState(false);
  const [produkSearch, setProdukSearch] = useState("");
  const [produkPage, setProdukPage] = useState(1);
  const PRODUK_PER_PAGE = 10;

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/koperasi/vendor"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filteredProduk = useMemo(() => {
    const q = produkSearch.trim().toLowerCase();
    if (!q) return produkList;
    return produkList.filter((p) => `${p.nama || ""} ${p.sku || ""} ${p.deskripsi || ""}`.toLowerCase().includes(q));
  }, [produkList, produkSearch]);
  useEffect(() => { setProdukPage(1); }, [produkSearch, produkTarget]); // reset ke halaman 1 tiap ganti vendor/pencarian
  const produkTotalPages = Math.max(1, Math.ceil(filteredProduk.length / PRODUK_PER_PAGE));
  const produkPageClamped = Math.min(produkPage, produkTotalPages);
  const produkStartIdx = (produkPageClamped - 1) * PRODUK_PER_PAGE;
  const pagedProduk = filteredProduk.slice(produkStartIdx, produkStartIdx + PRODUK_PER_PAGE);

  const openProduk = async (vendor) => {
    setProdukTarget(vendor);
    setProdukSearch("");
    setProdukLoading(true);
    try {
      const { data } = await api.get("/koperasi/produk", { params: { vendor_id: vendor.id } });
      setProdukList(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setProdukLoading(false);
  };

  const save = async () => {
    try {
      if (editId) await api.put(`/koperasi/vendor/${editId}`, form);
      else await api.post("/koperasi/vendor", form);
      toast.success("Tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/koperasi/vendor/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doCreateAccount = async () => {
    try {
      await api.post("/koperasi/vendor/users", { vendor_id: acctTarget.id, email: acctForm.email, password: acctForm.password });
      toast.success(`Akun vendor "${acctTarget.nama}" dibuat. Bagikan kredensial ke vendor.`);
      setAcctTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <Loading />;
  return (
    <>
      <div style={{ marginBottom: 10 }}>
        <button className="vb-btn-primary" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-vendor">
          <Plus className="w-3 h-3 inline mr-1" />Tambah Vendor
        </button>
        <span style={{ fontSize: 11, color: "#666", marginLeft: 10 }}>Setelah daftar, klik <strong>Buat Akun</strong> agar vendor bisa login & upload produk + jalankan mesin POS.</span>
      </div>
      {items.length === 0 ? <EmptyState message="Belum ada vendor" /> : (
        <table className="vb-table">
          <thead><tr><th>Nama Vendor</th><th>Pemilik</th><th>Kontak</th><th>Bank</th><th>Login</th><th>Status</th><th>Aksi</th></tr></thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.id}>
                <td><strong>{v.nama}</strong></td>
                <td>{v.pemilik}</td>
                <td>{v.kontak}</td>
                <td style={{ fontSize: 11 }}>{v.bank_nama} {v.bank_rekening && `· ${v.bank_rekening}`}<br />{v.bank_atas_nama}</td>
                <td>{v.has_user ? <span className="vb-badge vb-badge-approved">{v.user_email}</span> : <span className="vb-badge vb-badge-rejected">Belum</span>}</td>
                <td>{v.status_aktif ? <span className="vb-badge vb-badge-hadir">Aktif</span> : <span className="vb-badge vb-badge-tidakhadir">Nonaktif</span>}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {!v.has_user && (
                    <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => { setAcctTarget(v); setAcctForm({ email: v.email || "", password: "password123" }); }} data-testid={`vendor-acct-${v.id}`}>
                      <User className="w-3 h-3 inline mr-1" />Buat Akun
                    </button>
                  )}
                  <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => openProduk(v)} data-testid={`vendor-produk-${v.id}`}>
                    <Package className="w-3 h-3 inline mr-1" />Lihat Produk
                  </button>
                  <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => { setForm(v); setEditId(v.id); setOpen(true); }}><Pencil className="w-3 h-3" /></button>
                  <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(v)}><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit Vendor" : "Tambah Vendor"} width={560}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={save} data-testid="vendor-save">Simpan</button></>}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div className="vb-form-row" style={{ gridColumn: "1/3" }}><label className="vb-label">Nama Vendor*</label>
            <input className="vb-input" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} required /></div>
          <div className="vb-form-row"><label className="vb-label">Pemilik</label>
            <input className="vb-input" value={form.pemilik} onChange={(e) => setForm({ ...form, pemilik: e.target.value })} /></div>
          <div className="vb-form-row"><label className="vb-label">Kontak</label>
            <input className="vb-input" value={form.kontak} onChange={(e) => setForm({ ...form, kontak: e.target.value })} /></div>
          <div className="vb-form-row"><label className="vb-label">Email</label>
            <input className="vb-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div className="vb-form-row"><label className="vb-label">Alamat</label>
            <input className="vb-input" value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} /></div>
          <div className="vb-form-row"><label className="vb-label">Bank</label>
            <input className="vb-input" value={form.bank_nama} onChange={(e) => setForm({ ...form, bank_nama: e.target.value })} placeholder="BCA / BRI / Mandiri" /></div>
          <div className="vb-form-row"><label className="vb-label">No Rekening</label>
            <input className="vb-input" value={form.bank_rekening} onChange={(e) => setForm({ ...form, bank_rekening: e.target.value })} /></div>
          <div className="vb-form-row" style={{ gridColumn: "1/3" }}><label className="vb-label">Atas Nama</label>
            <input className="vb-input" value={form.bank_atas_nama} onChange={(e) => setForm({ ...form, bank_atas_nama: e.target.value })} /></div>
          <div className="vb-form-row" style={{ gridColumn: "1/3" }}>
            <label style={{ fontSize: 12 }}><input type="checkbox" checked={!!form.status_aktif} onChange={(e) => setForm({ ...form, status_aktif: e.target.checked })} /> Vendor aktif</label>
          </div>
        </div>
      </Dialog>

      <Dialog open={!!acctTarget} onClose={() => setAcctTarget(null)} title={`Buat Akun Login Vendor: ${acctTarget?.nama || ""}`} width={460}
        footer={<><button className="vb-btn" onClick={() => setAcctTarget(null)}>Batal</button><button className="vb-btn-primary" onClick={doCreateAccount} data-testid="vendor-acct-save">Buat Akun</button></>}>
        <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, marginBottom: 10, fontSize: 12 }}>
          Vendor akan login di <code>/vendor</code> untuk upload produk &amp; jalankan POS scan RFID.
        </div>
        <div className="vb-form-row"><label className="vb-label">Email*</label>
          <input type="email" className="vb-input" value={acctForm.email} onChange={(e) => setAcctForm({ ...acctForm, email: e.target.value })} required data-testid="vendor-acct-email" /></div>
        <div className="vb-form-row"><label className="vb-label">Password*</label>
          <input className="vb-input" value={acctForm.password} onChange={(e) => setAcctForm({ ...acctForm, password: e.target.value })} required data-testid="vendor-acct-password" />
        </div>
      </Dialog>

      <Dialog open={!!produkTarget} onClose={() => setProdukTarget(null)} title={`Produk Terdaftar: ${produkTarget?.nama || ""}`} width={980}
        footer={<button className="vb-btn" onClick={() => setProdukTarget(null)}>Tutup</button>}>
        {!produkLoading && produkList.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <input
              className="vb-input"
              style={{ width: "100%" }}
              value={produkSearch}
              onChange={(e) => setProdukSearch(e.target.value)}
              placeholder="Cari nama produk, SKU/barcode, atau deskripsi..."
              autoFocus
              data-testid="vendor-produk-search"
            />
          </div>
        )}
        {produkLoading ? <Loading /> : produkList.length === 0 ? <EmptyState message="Vendor ini belum mendaftarkan produk apa pun" /> : filteredProduk.length === 0 ? (
          <EmptyState message={`Tidak ada produk yang cocok dengan "${produkSearch}"`} />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="vb-table">
              <thead><tr><th>SKU / Barcode</th><th>Nama Produk</th><th>Deskripsi</th><th>Satuan</th><th style={{ textAlign: "right" }}>Harga</th><th>Status</th></tr></thead>
              <tbody>
                {pagedProduk.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontFamily: "Consolas, monospace" }}>{p.sku || "-"}</td>
                    <td><strong>{p.nama}</strong></td>
                    <td style={{ fontSize: 11, color: "#555" }}>{p.deskripsi || "-"}</td>
                    <td>{fmtUnit(p)}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtIDR(p.harga)}</td>
                    <td>{p.status_aktif ? <span className="vb-badge vb-badge-hadir">Aktif</span> : <span className="vb-badge vb-badge-tidakhadir">Nonaktif</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginator
              currentPage={produkPageClamped}
              totalPages={produkTotalPages}
              onChange={setProdukPage}
              totalItems={filteredProduk.length}
              startIdx={produkStartIdx}
              endIdx={Math.min(produkStartIdx + PRODUK_PER_PAGE, filteredProduk.length)}
              itemLabel="produk"
            />
          </div>
        )}
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus vendor "${del?.nama}"?`} />
    </>
  );
}

// ============ TRANSAKSI TAB ============
function TransaksiTab() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterBulan, setFilterBulan] = useState(new Date().toISOString().slice(0, 7));
  const [filterVendor, setFilterVendor] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterNama, setFilterNama] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { bulan: filterBulan };
      if (filterVendor) params.vendor_id = filterVendor;
      if (filterStatus) params.status = filterStatus;
      const [t, v] = await Promise.all([api.get("/koperasi/transaksi", { params }), api.get("/koperasi/vendor")]);
      setItems(t.data); setVendors(v.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBulan, filterVendor, filterStatus]);

  const filteredItems = useMemo(() => {
    const q = filterNama.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) => `${t.employee_nama || ""} ${t.no_karyawan || ""} ${t.no_rekening_koperasi || ""} ${t.vendor_nama || ""} ${t.no_transaksi || ""}`.toLowerCase().includes(q));
  }, [items, filterNama]);
  const totalAll = filteredItems.reduce((a, i) => a + i.total, 0);
  const selectedVendorLabel = vendors.find((v) => v.id === filterVendor)?.nama || "Semua Vendor";

  return (
    <>
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        <input type="month" className="vb-input" style={{ width: 160 }} value={filterBulan} onChange={(e) => setFilterBulan(e.target.value)} />
        <input className="vb-input" style={{ width: 220 }} value={filterNama} onChange={(e) => setFilterNama(e.target.value)} placeholder="Cari nama karyawan/vendor/no trx" data-testid="kop-filter-nama" />
        <select className="vb-select" style={{ width: 200 }} value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)}>
          <option value="">Semua Vendor</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.nama}</option>)}
        </select>
        <select className="vb-select" style={{ width: 150 }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">Semua Status</option>
          <option>Terhutang</option>
          <option>Lunas-Payroll</option>
        </select>
        <button type="button" className="vb-btn-primary" onClick={() => printAdminKoperasiReport(filteredItems, { bulan: filterBulan, vendorLabel: selectedVendorLabel, nama: filterNama.trim(), status: filterStatus })} disabled={loading || filteredItems.length === 0} data-testid="kop-print-trx-report">
          <FileSpreadsheet className="w-3 h-3 inline mr-1" />Print Laporan
        </button>
      </div>
      <div style={{ marginBottom: 10, padding: 8, background: "#e8f4ff", border: "1px solid #c4d9f1", fontSize: 12 }}>
        Total Transaksi tampil: <strong style={{ color: "#003399" }}>{fmtIDR(totalAll)}</strong> ({filteredItems.length} dari {items.length} transaksi)
      </div>
      {loading ? <Loading /> : items.length === 0 ? <EmptyState message="Belum ada transaksi" /> : filteredItems.length === 0 ? <EmptyState message="Transaksi tidak ditemukan sesuai filter nama" /> : (
        <div style={{ overflowX: "auto" }}>
          <table className="vb-table">
            <thead><tr><th>No. Trx</th><th>Tgl</th><th>Anggota</th><th>Vendor</th><th>Items</th><th style={{ textAlign: "right" }}>Total</th><th>Potong Periode</th><th>Status</th><th>Payment Vendor</th></tr></thead>
            <tbody>
              {filteredItems.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontFamily: "Consolas, monospace", fontWeight: 600, color: "#003399" }}>{t.no_transaksi}</td>
                  <td style={{ fontSize: 11 }}>{t.tanggal}</td>
                  <td>{t.employee_nama}<br /><span style={{ fontSize: 10, color: "#666" }}>{t.no_rekening_koperasi}</span></td>
                  <td>{t.vendor_nama}</td>
                  <td style={{ fontSize: 11 }}>{formatTrxItems(t.items)}</td>
                  <td style={{ textAlign: "right", fontWeight: 700 }}>{fmtIDR(t.total)}</td>
                  <td style={{ fontSize: 11, color: "#003399" }}>{t.periode_potong}</td>
                  <td>
                    {t.status === "Terhutang" ? <span className="vb-badge vb-badge-pending">Terhutang</span> : <span className="vb-badge vb-badge-approved">{t.status}</span>}
                  </td>
                  <td>{t.vendor_payment_status === "Lunas" ? <span className="vb-badge vb-badge-approved">Lunas ke Vendor</span> : <span className="vb-badge vb-badge-pending">Pending ke Vendor</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ============ PAYMENT VENDOR TAB ============
function PaymentVendorTab() {
  const [bulan, setBulan] = useState(new Date().toISOString().slice(0, 7));
  const [rekap, setRekap] = useState([]);
  const [reks, setReks] = useState([]);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ payment_date: new Date().toISOString().slice(0, 10), nominal: 0, metode: "Transfer", rekening_id: "", catatan: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, rk] = await Promise.all([api.get("/koperasi/rekap-vendor", { params: { bulan } }), api.get("/rekening")]);
      setRekap(r.data.rows || []); setReks(rk.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bulan]);

  const doPay = async () => {
    try {
      await api.post("/koperasi/vendor-payment", { vendor_id: payTarget.vendor_id, periode: bulan, ...payForm, nominal: parseFloat(payForm.nominal) });
      toast.success("Pembayaran vendor tercatat");
      setPayTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <div style={{ marginBottom: 10, display: "flex", gap: 6, alignItems: "center" }}>
        <label className="vb-label" style={{ marginBottom: 0 }}>Periode:</label>
        <input type="month" className="vb-input" style={{ width: 160 }} value={bulan} onChange={(e) => setBulan(e.target.value)} />
      </div>
      <div style={{ marginBottom: 10, padding: 8, background: "#fffbe6", border: "1px solid #d4a017", fontSize: 12 }}>
        Fivehoster akan membayar vendor berdasarkan rekap bulanan transaksi koperasi. Pilih rekening sumber dana saat bayar agar saldo rekening otomatis terdebet.
      </div>
      {loading ? <Loading /> : rekap.length === 0 ? <EmptyState message="Tidak ada transaksi pada periode ini" /> : (
        <table className="vb-table">
          <thead><tr><th>Vendor</th><th style={{ textAlign: "center" }}>Trx</th><th style={{ textAlign: "right" }}>Total</th><th style={{ textAlign: "right" }}>Sudah Dibayar</th><th style={{ textAlign: "right" }}>Outstanding</th><th>Aksi</th></tr></thead>
          <tbody>
            {rekap.map((r) => (
              <tr key={r.vendor_id}>
                <td><strong>{r.vendor_nama}</strong></td>
                <td style={{ textAlign: "center" }}>{r.count}</td>
                <td style={{ textAlign: "right" }}>{fmtIDR(r.total)}</td>
                <td style={{ textAlign: "right", color: "#4f8a10" }}>{fmtIDR(r.paid)}</td>
                <td style={{ textAlign: "right", color: r.outstanding > 0 ? "#d8000c" : "#4f8a10", fontWeight: 700 }}>{fmtIDR(r.outstanding)}</td>
                <td>
                  {r.outstanding > 0 && (
                    <button className="vb-btn-primary" style={{ padding: "2px 6px" }} onClick={() => { setPayTarget(r); setPayForm({ payment_date: new Date().toISOString().slice(0, 10), nominal: r.outstanding, metode: "Transfer", rekening_id: reks.find((x) => x.currency === "IDR")?.id || "", catatan: `Pembayaran transaksi koperasi periode ${bulan}` }); }} data-testid={`pay-vendor-${r.vendor_id}`}>
                      <Wallet className="w-3 h-3 inline mr-1" />Bayar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={!!payTarget} onClose={() => setPayTarget(null)} title={`Bayar Vendor: ${payTarget?.vendor_nama || ""}`} width={500}
        footer={<><button className="vb-btn" onClick={() => setPayTarget(null)}>Batal</button><button className="vb-btn-primary" onClick={doPay} data-testid="pay-vendor-save">Bayar</button></>}>
        {payTarget && (
          <>
            <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, marginBottom: 10, fontSize: 12 }}>
              Outstanding periode <strong>{bulan}</strong>: <strong>{fmtIDR(payTarget.outstanding)}</strong>
            </div>
            <div className="vb-form-row"><label className="vb-label">Tanggal Bayar*</label>
              <input type="date" className="vb-input" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></div>
            <div className="vb-form-row"><label className="vb-label">Nominal*</label>
              <RupiahInput value={payForm.nominal} onChange={(v) => setPayForm({ ...payForm, nominal: v })} testId="pay-vendor-nominal" /></div>
            <div className="vb-form-row"><label className="vb-label">Metode</label>
              <select className="vb-select" value={payForm.metode} onChange={(e) => setPayForm({ ...payForm, metode: e.target.value })}><option>Transfer</option><option>Cash</option></select></div>
            <div className="vb-form-row"><label className="vb-label">Sumber Dana (Rekening) — opsional</label>
              <select className="vb-select" value={payForm.rekening_id} onChange={(e) => setPayForm({ ...payForm, rekening_id: e.target.value })}>
                <option value="">- tidak dipotong saldo rekening -</option>
                {reks.filter((r) => r.currency === "IDR").map((r) => <option key={r.id} value={r.id}>{r.nama} (saldo {fmtIDR(r.saldo)})</option>)}
              </select></div>
            <div className="vb-form-row"><label className="vb-label">Catatan</label>
              <textarea rows={2} className="vb-textarea" value={payForm.catatan} onChange={(e) => setPayForm({ ...payForm, catatan: e.target.value })} /></div>
          </>
        )}
      </Dialog>
    </>
  );
}

// ============ PAYROLL POTONGAN TAB ============
function PayrollPotonganTab() {
  const [periode, setPeriode] = useState(new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/koperasi/payroll-potongan", { params: { periode_bulan: periode } });
      setRows(data.rows || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periode]);

  const apply = async () => {
    if (!window.confirm(`Tandai semua transaksi koperasi periode ${periode} sebagai sudah dipotong dari payroll?`)) return;
    try {
      const { data } = await api.post(`/koperasi/apply-payroll?periode_bulan=${periode}`);
      toast.success(`${data.updated} transaksi ditandai sudah dipotong payroll`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const total = rows.reduce((a, r) => a + r.total_potong, 0);

  return (
    <>
      <div style={{ marginBottom: 10, display: "flex", gap: 6, alignItems: "center" }}>
        <label className="vb-label" style={{ marginBottom: 0 }}>Periode Gaji (YYYY-MM):</label>
        <input type="month" className="vb-input" style={{ width: 160 }} value={periode} onChange={(e) => setPeriode(e.target.value)} />
        <button className="vb-btn-primary" onClick={apply} disabled={rows.length === 0} data-testid="apply-payroll-kop">
          <CreditCard className="w-3 h-3 inline mr-1" />Tandai Sudah Dipotong Gaji
        </button>
      </div>
      <div style={{ marginBottom: 10, padding: 8, background: "#fffbe6", border: "1px solid #d4a017", fontSize: 12 }}>
        Daftar potongan koperasi yang harus dipotong di gaji periode <strong>{periode}</strong> (tanggal 17). Setelah payroll dibayar, klik tombol di atas untuk mark transaksi sebagai Lunas-Payroll.
      </div>
      {loading ? <Loading /> : rows.length === 0 ? <EmptyState message="Tidak ada potongan untuk periode ini" /> : (
        <table className="vb-table">
          <thead><tr><th>No. Karyawan</th><th>Nama Karyawan</th><th style={{ textAlign: "center" }}>Jumlah Trx</th><th style={{ textAlign: "right" }}>Total Potong</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.employee_id}>
                <td style={{ fontFamily: "Consolas, monospace" }}>{r.no_karyawan}</td>
                <td><strong>{r.employee_nama}</strong></td>
                <td style={{ textAlign: "center" }}>{r.jumlah_transaksi}</td>
                <td style={{ textAlign: "right", color: "#d8000c", fontWeight: 700 }}>{fmtIDR(r.total_potong)}</td>
              </tr>
            ))}
            <tr style={{ background: "#ececec", fontWeight: 700 }}>
              <td colSpan={3} style={{ textAlign: "right" }}>TOTAL POTONGAN KOPERASI:</td>
              <td style={{ textAlign: "right", color: "#d8000c" }}>{fmtIDR(total)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </>
  );
}

// ============ Paginator: 10 per page + tombol angka + ellipsis (pola sama dengan client/Dashboard.jsx) ============
function Paginator({ currentPage, totalPages, onChange, totalItems, startIdx, endIdx, itemLabel = "data" }) {
  if (totalPages <= 1) return null;
  const pages = [];
  const push = (v) => pages.push(v);
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) push(i);
  } else {
    push(1);
    if (currentPage > 4) push("...");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) push(i);
    if (currentPage < totalPages - 3) push("...");
    push(totalPages);
  }

  return (
    <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", fontSize: 12 }} data-testid="paginator">
      <div style={{ color: "#666" }}>
        Menampilkan <strong>{startIdx + 1}–{endIdx}</strong> dari <strong>{totalItems}</strong> {itemLabel}
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        <button className="vb-btn" disabled={currentPage <= 1} onClick={() => onChange(currentPage - 1)} data-testid="page-prev">‹ Prev</button>
        {pages.map((p, i) => (
          p === "..." ? (
            <span key={`e-${i}`} style={{ padding: "4px 6px", color: "#666" }}>...</span>
          ) : (
            <button
              key={p}
              className={p === currentPage ? "vb-btn-primary" : "vb-btn"}
              style={{ padding: "2px 9px", minWidth: 28, fontWeight: p === currentPage ? 700 : 400 }}
              onClick={() => onChange(p)}
              data-testid={`page-${p}`}
            >
              {p}
            </button>
          )
        ))}
        <button className="vb-btn" disabled={currentPage >= totalPages} onClick={() => onChange(currentPage + 1)} data-testid="page-next">Next ›</button>
      </div>
    </div>
  );
}
